import { MORE_FILTERS_BOOLEAN_OPTIONS } from 'client-portal/utils/clinicianSearch/more-filters-boolean-options';
import {
  parseCommaSeparatedInts,
  parseCommaSeparatedStrings,
} from 'client-portal/utils/clinicianSearch/comma-query-param';
import { toQueryParamKeys } from 'client-portal/utils/clinicianSearch/facets/query-param-aliases';

export const GROUP_FLOW_FILTER_OBJECT = 'Group Flow Filter';

export const GROUP_FLOW_FILTER_EVENTS = {
  applied: 'Group Flow - Filter Applied',
  cleared: 'Group Flow - Filter Cleared',
  typeSelected: 'Group Flow - Filter Type Selected',
};

const PILL_KEY_BY_PARAM = {
  name: 'name',
  specialty: 'specialty',
  service: 'approaches',
  officeIds: 'location',
  cptCodes: 'service',
  availableDays: 'availability',
  availableTimes: 'availability',
  insurance: 'insurance',
  ageGroup: 'ageGroup',
  treatmentType: 'moreFilters',
  ...Object.fromEntries(MORE_FILTERS_BOOLEAN_OPTIONS.map(spec => [spec.param, 'moreFilters'])),
};

const DAY_ANALYTICS_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const TIME_BUCKET_ANALYTICS_LABELS = ['Early morning', 'Morning', 'Afternoon', 'Evening'];

const MORE_FILTERS_BOOLEAN_ANALYTICS_LABELS = {
  availableNights: 'Available evenings',
  availableWeekends: 'Available weekends',
  inPerson: 'In-person',
  freeConsultation: 'Offers free consultation',
};

function isActiveFilterValue(value) {
  return ![null, undefined, '', false].includes(value);
}

export function officeFilterLabel(office) {
  if (!office) {
    return '';
  }

  let name = String(office.name ?? '').trim();

  if (name) {
    return name;
  }

  let parts = [];

  if (office.isPublic && office.street) {
    parts.push(String(office.street).trim());
  }

  let cityState = [office.city, office.state]
    .map(cityOrState => String(cityOrState ?? '').trim())
    .filter(Boolean)
    .join(', ');
  let zip = String(office.formattedZip ?? office.zip ?? '').trim();
  let cityStateZip = [cityState, zip].filter(Boolean).join(' ');

  if (cityStateZip) {
    parts.push(cityStateZip);
  }

  return parts.join(', ');
}

function labeledInts(value, labels) {
  return parseCommaSeparatedInts(value)
    .map(id => labels[id] ?? String(id))
    .join(',');
}

function labeledIds(value, resolveLabel) {
  let labels = parseCommaSeparatedStrings(value).map(id => resolveLabel?.(id) || id);
  return [...new Set(labels)].join(',');
}

function formatParamValue(param, value, { officeLabel, serviceLabel } = {}) {
  if (param === 'availableDays') {
    return labeledInts(value, DAY_ANALYTICS_LABELS);
  }

  if (param === 'availableTimes') {
    return labeledInts(value, TIME_BUCKET_ANALYTICS_LABELS);
  }

  if (param === 'officeIds') {
    return labeledIds(value, officeLabel);
  }

  if (param === 'cptCodes') {
    return labeledIds(value, serviceLabel);
  }

  if (MORE_FILTERS_BOOLEAN_ANALYTICS_LABELS[param]) {
    return MORE_FILTERS_BOOLEAN_ANALYTICS_LABELS[param];
  }

  return String(value);
}

function comparableFilterValue(value) {
  return isActiveFilterValue(value) ? String(value) : '';
}

function filterParamsUnchanged(incoming, current) {
  let params = Object.keys(incoming).filter(param => param in PILL_KEY_BY_PARAM);
  return params.every(
    param => comparableFilterValue(incoming[param]) === comparableFilterValue(current[param])
  );
}

function changedFilterValue(urlParams, filterType, labels) {
  if (filterType === 'all') {
    return null;
  }

  let values = Object.keys(urlParams)
    .filter(param => PILL_KEY_BY_PARAM[param] === filterType)
    .filter(param => isActiveFilterValue(urlParams[param]))
    .map(param => formatParamValue(param, urlParams[param], labels));

  return values.join(',') || null;
}

export function classifyGroupFlowFilterChange(queryParams = {}, options = {}) {
  let incoming = toQueryParamKeys(queryParams);
  let current = toQueryParamKeys(options.currentParams ?? {});
  let filterTypes = new Set();
  let applied = false;

  for (let param in PILL_KEY_BY_PARAM) {
    if (!(param in incoming)) {
      continue;
    }

    filterTypes.add(PILL_KEY_BY_PARAM[param]);
    applied ||= isActiveFilterValue(incoming[param]);
  }

  if (!filterTypes.size || filterParamsUnchanged(incoming, current)) {
    return null;
  }

  let filterType = filterTypes.size === 1 ? [...filterTypes][0] : 'all';
  let change = {
    eventName: applied ? GROUP_FLOW_FILTER_EVENTS.applied : GROUP_FLOW_FILTER_EVENTS.cleared,
    action: applied ? 'Applied' : 'Cleared',
    filterType,
  };

  if (applied) {
    change.filterValue = changedFilterValue(incoming, filterType, options);
  }

  return change;
}

function trackIfDiscoveryEnabled(analytics, isDiscoveryEnabled, eventName, properties) {
  if (!isDiscoveryEnabled || !properties.filter_type) {
    return;
  }

  analytics?.selectiveTrack(eventName, {
    object: GROUP_FLOW_FILTER_OBJECT,
    ...properties,
  });
}

export function trackGroupFlowFilterChange(
  analytics,
  isDiscoveryEnabled,
  queryParams = {},
  labels = {}
) {
  let change = classifyGroupFlowFilterChange(queryParams, labels);
  if (!change) {
    return;
  }

  let properties = {
    action: change.action,
    'filter_type': change.filterType,
  };

  if (change.action === 'Applied') {
    properties = {
      ...properties,
      'filter_value': change.filterValue,
    };
  }

  trackIfDiscoveryEnabled(analytics, isDiscoveryEnabled, change.eventName, properties);
}

export function trackGroupFlowFilterTypeSelected(analytics, isDiscoveryEnabled, filterType) {
  trackIfDiscoveryEnabled(analytics, isDiscoveryEnabled, GROUP_FLOW_FILTER_EVENTS.typeSelected, {
    action: 'Selected',
    'filter_type': filterType,
  });
}
